import type { ZoneWorkingHoursDto } from '../../api/workingHours';
import {
  ALL_DAY_CLOSE_TIME,
  ALL_DAY_OPEN_TIME,
  ALL_DAYS_MASK,
  DEFAULT_CLOSE_TIME,
  DEFAULT_OPEN_TIME,
  isAllDayRange,
  isSingleFullWeekAllDayGroup,
  nextExceptionLocalId,
  nextGroupLocalId,
  type ScheduleExceptionDraft,
  type ScheduleGroup,
} from './workingHoursState';

export function rowsToGroups(rows: ZoneWorkingHoursDto[]): ScheduleGroup[] {
  const weeklyRows = rows.filter(r => r.date === null);
  const [r0] = weeklyRows;

  // API represents "open 24×7" as either no weekly rows or a single all-day full-week row.
  // Both cases normalise to the canonical explicit 24×7 group.
  if (
    !r0
    || (weeklyRows.length === 1
        && r0.weekdays_mask === ALL_DAYS_MASK
        && !!r0.open_time && !!r0.close_time
        && isAllDayRange(r0.open_time.slice(0, 5), r0.close_time.slice(0, 5)))
  ) {
    return [{
      localId: nextGroupLocalId(),
      ...(r0?.id ? { serverId: r0.id } : {}),
      mask: ALL_DAYS_MASK,
      openTime: ALL_DAY_OPEN_TIME,
      closeTime: ALL_DAY_CLOSE_TIME,
    }];
  }

  return weeklyRows.map(r => ({
    localId: nextGroupLocalId(),
    serverId: r.id,
    mask: r.weekdays_mask,
    openTime: r.open_time?.slice(0, 5) ?? DEFAULT_OPEN_TIME,
    closeTime: r.close_time?.slice(0, 5) ?? DEFAULT_CLOSE_TIME,
    staffArrivalMinutes: r.staff_arrival_minutes ?? null,
  }));
}

export function rowToExceptionDrafts(
  exceptionRows: ZoneWorkingHoursDto[],
  baseGroups: ScheduleGroup[],
): { groups: ScheduleGroup[]; exceptions: ScheduleExceptionDraft[] } {
  const groups = [...baseGroups];
  const exceptions: ScheduleExceptionDraft[] = [];

  for (const row of exceptionRows) {
    const openTime = row.open_time?.slice(0, 5) ?? null;
    const closeTime = row.close_time?.slice(0, 5) ?? null;

    if (!openTime || !closeTime) {
      exceptions.push({
        localId: nextExceptionLocalId(),
        serverId: row.id,
        date: row.date!,
        openTime: null,
        closeTime: null,
        periodLocalId: 'off',
      });
      continue;
    }

    let group = groups.find(g => g.mask !== 0 && g.openTime === openTime && g.closeTime === closeTime);
    if (!group) {
      group = { localId: nextGroupLocalId(), mask: 0, openTime, closeTime };
      groups.push(group);
    }

    exceptions.push({
      localId: nextExceptionLocalId(),
      serverId: row.id,
      date: row.date!,
      openTime,
      closeTime,
      periodLocalId: group.localId,
    });
  }

  return { groups, exceptions };
}

function toApiCloseTime(openTime: string, closeTime: string): string {
  if (openTime === closeTime) {
    const [h, m] = openTime.split(':').map(Number);
    const total = (h * 60 + m + 24 * 60 - 1) % (24 * 60);
    const hh = String(Math.floor(total / 60)).padStart(2, '0');
    const mm = String(total % 60).padStart(2, '0');
    return `${hh}:${mm}:59`;
  }
  if (openTime === ALL_DAY_OPEN_TIME && closeTime === ALL_DAY_CLOSE_TIME) return '23:59:59';
  return `${closeTime}:00`;
}

export function groupToPayload(g: ScheduleGroup, zoneId: string) {
  return {
    zone: zoneId,
    date: null as null,
    open_time: `${g.openTime}:00`,
    close_time: toApiCloseTime(g.openTime, g.closeTime),
    weekdays_mask: g.mask,
    staff_arrival_minutes: g.staffArrivalMinutes ?? null,
  };
}

export function exceptionToPayload(
  ex: ScheduleExceptionDraft,
  zoneId: string,
): Omit<ZoneWorkingHoursDto, 'id'> {
  return {
    zone: zoneId,
    date: ex.date,
    open_time: ex.openTime ? `${ex.openTime}:00` : null,
    close_time: ex.openTime && ex.closeTime ? toApiCloseTime(ex.openTime, ex.closeTime) : null,
    weekdays_mask: ALL_DAYS_MASK,
  };
}

export function normalizeExceptionsWithGroups(
  draft: ScheduleGroup[],
  draftExceptions: ScheduleExceptionDraft[],
): ScheduleExceptionDraft[] {
  return draftExceptions.map(ex => {
    if (ex.periodLocalId === 'off') return { ...ex, openTime: null, closeTime: null };
    const group = draft.find(g => g.localId === ex.periodLocalId);
    if (!group) return { ...ex, openTime: null, closeTime: null, periodLocalId: 'off' as const };
    return { ...ex, openTime: group.openTime, closeTime: group.closeTime };
  });
}

export function resolveGroupsToPersist(groups: ScheduleGroup[]): ScheduleGroup[] {
  const activeGroups = groups.filter(g => g.mask !== 0);
  if (isSingleFullWeekAllDayGroup(activeGroups)) return []; // 24×7 → empty rows (API convention)
  if (activeGroups.length > 0) return activeGroups;
  // Closed state: must write a sentinel row (weekdays_mask=0) so the API can distinguish closed
  // from 24×7 (both would otherwise be empty rows). Preserve existing mask-0 entries so their
  // serverId survives the diff; create a fresh one only when there are none (e.g. the zone was
  // previously 24×7 and had no stored row at all).
  if (groups.length > 0) return groups;
  return [{ localId: nextGroupLocalId(), mask: 0, openTime: DEFAULT_OPEN_TIME, closeTime: DEFAULT_CLOSE_TIME }];
}

// ── Save-ops builder ──────────────────────────────────────────────────────────

export type SaveOp =
  | { op: 'delete'; id: string }
  | { op: 'create'; payload: Omit<ZoneWorkingHoursDto, 'id'> }
  | { op: 'update'; id: string; payload: Omit<ZoneWorkingHoursDto, 'id'> };

export function buildSaveOps(
  serverGroups: ScheduleGroup[],
  serverExceptionRows: ZoneWorkingHoursDto[],
  newGroups: ScheduleGroup[],
  newExceptions: ScheduleExceptionDraft[],
  zoneId: string,
): SaveOp[] {
  const ops: SaveOp[] = [];
  const groupsToPersist = resolveGroupsToPersist(newGroups);

  for (const sg of serverGroups) {
    if (sg.serverId && !groupsToPersist.find(g => g.serverId === sg.serverId)) {
      ops.push({ op: 'delete', id: sg.serverId });
    }
  }
  for (const g of groupsToPersist) {
    if (!g.serverId) {
      ops.push({ op: 'create', payload: groupToPayload(g, zoneId) });
    } else {
      const orig = serverGroups.find(sg => sg.serverId === g.serverId);
      if (orig && (orig.mask !== g.mask || orig.openTime !== g.openTime || orig.closeTime !== g.closeTime)) {
        ops.push({ op: 'update', id: g.serverId, payload: groupToPayload(g, zoneId) });
      }
    }
  }

  for (const row of serverExceptionRows) {
    if (!newExceptions.find(ex => ex.serverId === row.id)) {
      ops.push({ op: 'delete', id: row.id });
    }
  }
  for (const ex of newExceptions) {
    const payload = exceptionToPayload(ex, zoneId);
    if (!ex.serverId) {
      ops.push({ op: 'create', payload });
    } else {
      const orig = serverExceptionRows.find(r => r.id === ex.serverId);
      if (orig && (orig.date !== payload.date || orig.open_time !== payload.open_time || orig.close_time !== payload.close_time)) {
        ops.push({ op: 'update', id: ex.serverId, payload });
      }
    }
  }

  return ops;
}
